import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabaseClient';
import toast from 'react-hot-toast';

// Utility to load Razorpay script
const loadScript = (src) => {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CheckoutButton = ({ planId, couponCode, buttonText, className }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    if (!user) {
      toast.error('Please login first');
      return;
    }

    setLoading(true);

    try {
      // Call Supabase Edge Function to create order
      const { data, error } = await supabase.functions.invoke('create-order', {
        body: { planId, couponCode: couponCode || null }
      });

      if (error || !data.success) {
        throw new Error(error?.message || 'Failed to create order');
      }

      // If in test mode, auto-verify payment
      if (data.testMode) {
        toast.loading('Test mode: Activating premium...');
        
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-payment', {
          body: {
            razorpay_order_id: data.orderId,
            razorpay_payment_id: 'test_payment_' + Date.now(),
            razorpay_signature: 'test_signature'
          }
        });

        toast.dismiss();
        
        if (verifyError || !verifyData.success) {
          toast.error('Test activation failed');
        } else {
          toast.success('✅ Premium activated! Test mode successful.');
          setTimeout(() => window.location.reload(), 1500);
        }
        setLoading(false);
        return;
      }

      // Real Razorpay flow (when you configure it)
      // Load Razorpay script
      const scriptLoaded = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
      if (!scriptLoaded) {
        toast.error('Payment service unavailable');
        setLoading(false);
        return;
      }

      const { orderId, amount, currency, keyId } = data;

      const options = {
        key: keyId,
        amount: amount * 100,
        currency: currency,
        name: 'Fast Global Resume',
        description: `Plan: ${planId}`,
        order_id: orderId,
        handler: async (response) => {
          const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-payment', {
            body: {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }
          });

          if (verifyError || !verifyData.success) {
            toast.error('Payment verification failed');
          } else {
            toast.success('Payment successful! Premium activated.');
            setTimeout(() => window.location.reload(), 1500);
          }
        },
        prefill: {
          name: user.user_metadata?.full_name || '',
          email: user.email,
        },
        theme: {
          color: '#2563EB'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handlePayment}
      disabled={loading}
      className={className || 'w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50'}
    >
      {loading ? 'Processing...' : buttonText || 'Subscribe Now'}
    </button>
  );
};

export default CheckoutButton;